import { motion, AnimatePresence } from 'framer-motion';
import { Flame } from 'lucide-react';

interface StreakBadgeProps {
    current: number;
    multiplier: number;
    compact?: boolean;
}

export function StreakBadge({ current, multiplier, compact = false }: StreakBadgeProps) {
    const isOnFire = current >= 3;
    const hasMultiplier = multiplier > 1;

    if (compact) {
        return (
            <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-sm ${isOnFire
                    ? 'bg-orange-500/10 text-orange-500'
                    : 'bg-muted text-muted-foreground'
                }`}>
                <Flame className={`w-4 h-4 ${isOnFire ? 'animate-pulse' : ''}`} />
                <span className="font-medium">{current}</span>
                {hasMultiplier && (
                    <span className="text-xs opacity-75">×{multiplier.toFixed(1)}</span>
                )}
            </div>
        );
    }

    return (
        <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative"
        >
            <div className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isOnFire
                    ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30'
                    : 'bg-muted/50 border border-border'
                }`}>
                {/* Flame Icon */}
                <div className={`relative w-12 h-12 rounded-full flex items-center justify-center ${isOnFire
                        ? 'bg-gradient-to-br from-orange-500 to-red-500'
                        : 'bg-muted'
                    }`}>
                    <Flame className={`w-6 h-6 ${isOnFire ? 'text-white' : 'text-muted-foreground'}`} />

                    {/* Fire animation */}
                    <AnimatePresence>
                        {isOnFire && (
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ repeat: Infinity, duration: 1.5 }}
                                className="absolute inset-0 rounded-full bg-orange-500/30 blur-md"
                            />
                        )}
                    </AnimatePresence>
                </div>

                {/* Info */}
                <div>
                    <div className="flex items-baseline gap-2">
                        <span className={`text-2xl font-bold ${isOnFire ? 'text-orange-500' : 'text-foreground'}`}>
                            {current}
                        </span>
                        <span className="text-sm text-muted-foreground">
                            {current === 1 ? 'dia' : 'dias'}
                        </span>
                    </div>

                    {hasMultiplier ? (
                        <p className="text-sm text-orange-500 font-medium">
                            Bônus ×{multiplier.toFixed(1)} ativo!
                        </p>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            {current === 0 ? 'Comece seu streak!' : 'Continue assim!'}
                        </p>
                    )}
                </div>
            </div>
        </motion.div>
    );
}
